import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Shield, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import SectionHeading from '../components/SectionHeading';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const supportTypes = [
  { id: 'food', name: 'Food / Groceries' },
  { id: 'clothing', name: 'Clothing / Winter Essentials' },
  { id: 'medical', name: 'Medical Assistance' },
  { id: 'education', name: 'Education Support' },
  { id: 'shelter', name: 'Housing / Shelter' },
  { id: 'financial', name: 'Financial Assistance' },
  { id: 'other', name: 'Other Support' },
];

const SupportPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    support_type: '',
    description: '',
    is_urgent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.support_type || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/support-requests`, formData);
      setIsSubmitted(true);
      toast.success('Your request has been submitted confidentially.');
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-8"
          data-testid="support-success"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Request Received
          </h1>
          <p className="text-muted-foreground mb-6">
            Your support request has been submitted confidentially. Our team will review it and 
            contact you within 3-5 business days.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            For urgent matters, please reach out directly via email or WhatsApp.
          </p>
          <Button onClick={() => window.location.href = '/'} className="rounded-full">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              We're Here to Help
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Request <span className="text-gradient">Support</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              If you or your family needs assistance, please fill out this confidential form. 
              All requests are handled with care and discretion.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 lg:py-32" data-testid="support-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="support-form">
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    All information submitted is kept strictly confidential and will only be used 
                    to assess and provide support.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      className="h-12 rounded-xl"
                      data-testid="support-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(123) 456-7890"
                      className="h-12 rounded-xl"
                      data-testid="support-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 rounded-xl"
                    data-testid="support-email"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support_type">Type of Support Needed *</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, support_type: value })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="support-type">
                      <SelectValue placeholder="Select type of support" />
                    </SelectTrigger>
                    <SelectContent>
                      {supportTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Please Describe Your Situation *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please provide details about your situation and the support you need..."
                    className="min-h-[150px] rounded-xl"
                    data-testid="support-description"
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="urgent" 
                    checked={formData.is_urgent}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_urgent: checked })}
                    data-testid="support-urgent"
                  />
                  <Label htmlFor="urgent" className="text-sm text-muted-foreground cursor-pointer">
                    This is an urgent request
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-lg font-semibold"
                  data-testid="support-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-5 w-5" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Info Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground mb-4">Eligibility</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Residents of Barrie and Simcoe County</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Individuals and families facing financial hardship</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Orphans and families caring for orphans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Newcomers to Canada needing settlement support</span>
                  </li>
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground mb-4">Response Time</h3>
                <p className="text-muted-foreground mb-4">
                  We aim to respond to all requests within <strong className="text-foreground">3-5 business days</strong>.
                </p>
                <p className="text-muted-foreground">
                  Urgent requests are prioritized and typically addressed within 24-48 hours.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-secondary/10 border border-secondary/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">For Emergencies</h4>
                    <p className="text-sm text-muted-foreground">
                      If you are in immediate danger or facing a life-threatening emergency, 
                      please call 911 immediately.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <blockquote className="text-lg italic text-foreground mb-3">
                  "Whoever relieves a believer's distress of the distressful aspects of this world, 
                  Allah will rescue them from a difficulty of the difficulties of the Hereafter."
                </blockquote>
                <p className="text-sm text-muted-foreground">— Prophet Muhammad (PBUH)</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
